import { memo, useCallback } from 'react'
import { Minus, Plus } from 'lucide-react'
import { Label } from '@shared/components/ui/label'
import { cn } from '@shared/lib/utils'

interface QuantityInputProps {
  id?: string
  label?: string
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  className?: string
}

export const QuantityInput = memo<QuantityInputProps>(
  function QuantityInput({
    id = "quantity",
    label = "Qty",
    value,
    onChange,
    min = 1,
    max = 9999,
    className = ""
  }) {
    const handleDecrement = useCallback(() => {
      if (value > min) {
        onChange(value - 1)
      }
    }, [value, min, onChange])

    const handleIncrement = useCallback(() => {
      if (value < max) {
        onChange(value + 1)
      }
    }, [value, max, onChange])

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = parseInt(e.target.value, 10)
      if (!isNaN(newValue) && newValue >= min && newValue <= max) {
        onChange(newValue)
      }
    }, [min, max, onChange])

    const isDecrementDisabled = value <= min
    const isIncrementDisabled = value >= max

    return (
      <div className={cn("flex flex-col gap-1.5", className)}>
        {label && (
          <Label htmlFor={id} className="text-xs font-medium text-foreground">
            {label}
          </Label>
        )}
        <div className="border border-input rounded-[10px] h-9 flex items-center justify-between px-2">
          {/* Decrement Button */}
          <button
            type="button"
            onClick={handleDecrement}
            disabled={isDecrementDisabled}
            className={cn(
              "border border-border rounded-md p-1 transition-all",
              isDecrementDisabled
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-muted cursor-pointer"
            )}
            aria-label="Decrease quantity"
          >
            <Minus className="h-4 w-4 text-muted-foreground" />
          </button>

          {/* Number Input */}
          <input
            id={id}
            type="number"
            value={value}
            onChange={handleInputChange}
            min={min}
            max={max}
            className="flex-1 text-center text-sm font-medium text-foreground bg-transparent border-none outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            aria-label={label || "Quantity"}
          />

          {/* Increment Button */}
          <button
            type="button"
            onClick={handleIncrement}
            disabled={isIncrementDisabled}
            className={cn(
              "border border-border rounded-md p-1 transition-all",
              isIncrementDisabled
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-muted cursor-pointer"
            )}
            aria-label="Increase quantity"
          >
            <Plus className="h-4 w-4 text-foreground" />
          </button>
        </div>
      </div>
    )
  }
)

QuantityInput.displayName = 'QuantityInput'
